import keyBy from 'lodash/keyBy';
import filter from 'lodash/filter';
import fromPairs from 'lodash/fromPairs';
import findIndex from 'lodash/findIndex';
import cloneDeep from 'lodash/cloneDeep';

import { fallbackToArray, ensureQueryDefaults } from '~/misc/search';
import { datesWithoutPast } from '~/misc/dates';
import { inputValuesAll } from '~/misc/equipment';
import getStorageValue from '~/misc/getStorageValue';
import safeJSONParse from '~/misc/safeJSONParse';
import resourceId from '~/misc/resourceId';
import { checked, checkedAll, confirmCheckedAll } from '~/misc/checked';

import A from './types';
import nowTypes from '~/modules/now/types';
import LOADS from '~/modules/loads/types';
import WINDOW from '~/modules/window/types';
import { statistyChecked } from '~/misc/checked';
import pickBy from 'lodash/pickBy';


const getResourceIdentity = (queryCompleted) => resourceId('loadsSearch', {
  query: queryCompleted,
});
// define loadsSearch initial value is here
const initialState = {
  query: ensureQueryDefaults({
    equipment: fallbackToArray(safeJSONParse(getStorageValue('equipment'))).filter(i => inputValuesAll.includes(i)),
    loadSize: 'full',
  }),
  identity: getResourceIdentity(null),
  indicatorData: {},
  capacityRatioData: [],
  status:'loading'
};

const searchStatuses = {
  [A.LOADS_SEARCH]: 'loading',
  [A.LOADS_SORT]: 'sorting',
  [A.LOADS_MORE]: 'fetchingMore',
  [A.LOADS_REFRESHED]: 'refreshing',
};

const countPresence = (index, list, keyField) =>
  filter(list, (item) => index[item[keyField]]);

const countNotDone = (list, deleted, done) =>
  filter(list, (item) => !deleted[item.id] && !done[item.id]).length;

const getCounters = ({ originalTotal, addedRemoved, allRemoved, allAdded, addedUnreaded, addedUnlooked }) => {
  const originalRemoved = allRemoved - addedRemoved;
  const allWithAdded = (originalTotal - originalRemoved) + (allAdded - addedRemoved);
  const allWithoutUnreaded = allWithAdded - addedUnreaded;
  const allWithoutUnlooked = allWithAdded - addedUnlooked;

  return {
    allWithAdded,
    allWithoutUnreaded,
    allWithoutUnlooked,
    addedUnreaded,
    addedUnlooked,
  };
};


const getQueryWithoutPastDays = (query, nowDate) => {
  if (!query || !query.date || !query.date.length) {
    return query;
  }

  return {
    ...query,
    date: datesWithoutPast(query.date, nowDate),
  };
};

const mergeObjectForce = (origin, merged) => {
  return {
    ...origin,
    ...merged
  }
}

const mergeObjectSafe = (origin, merged) => {
  const safeMerged = pickBy(merged, (value, key) => origin.hasOwnProperty(key));
  return mergeObjectForce(origin, safeMerged);
}

const getCountPairs = (item, i) => ([item.id, i]);

const getOrderIndex = (main, added) =>
  fromPairs([].concat(
    main.map(getCountPairs),
    added.map(getCountPairs)
  ));

const reducer = (state, action, fullState) => {
  switch (action.type) {
    case nowTypes.NOW_DATE_CHANGED: {
      const completed = getQueryWithoutPastDays(state.queryCompleted, action.payload);

      return {
        query: getQueryWithoutPastDays(state.query, action.payload),
        queryCompleted: completed,
        identity: getResourceIdentity(completed),
      };
    }
    case A.SEARCH_CRITERIA: {
      return {
        query: action.payload,
      };
    }
    case A.LOADS_CRITERIA_COMPLETED: {
      if (action.error || action.pending) {
        return {
          queryCompleted: null,
          identity: getResourceIdentity(null),
        };
      }
      return {
        queryCompleted: action.payload,
        identity: getResourceIdentity(action.payload),
      };
    }
    case A.LOADS_REFRESHED: {
      if (action.pending) {
        return {
          status: searchStatuses[action.type],
        };
      }

      if (!action.payload) {
        return {
          status: 'ok',
          updatesEnabled: false,
          tooManyUpdates: true,
        };
      }

      const windowHasFocus = fullState.window.hasFocus;

      const toAdd = (action.payload.new_shipments || []).map(item => ({
        ...item,
        identity: resourceId('loadsSearchItem', {
          id: item.id,
        }, state.identity),
        parentIdentity: state.identity,
      }));

      const addedItemsFlat = [
        ...toAdd,
        ...state.addedItemsFlat,
      ];

      const deletedIndex = {
        ...state.deletedIndex,
        ...keyBy(action.payload.deleted_shipments),
      };

      const justAddedIndex = keyBy(toAdd, 'id');

      const itemsIndex = {
        ...state.itemsIndex,
        ...justAddedIndex,
      };

      const previewedIndex = windowHasFocus ? itemsIndex : state.previewedIndex;

      const counts = {
        originalTotal: state.counts.originalTotal,
        addedRemoved: countPresence(addedItemsFlat, deletedIndex, 'id'),
        allRemoved: Object.keys(deletedIndex).length,
        allAdded: addedItemsFlat.length,
        addedUnreaded: countNotDone(addedItemsFlat, deletedIndex, fullState.loads.viewed),
        addedUnlooked: countNotDone(addedItemsFlat, deletedIndex, previewedIndex),
      };

      return {
        fetchedAt: new Date(),
        status: 'ok',
        search_since: action.payload.search_since,
        itemsIndex: itemsIndex,
        previewedIndex: previewedIndex,
        addedItemsFlat: addedItemsFlat,
        addedItemsFlatIndex: {
          ...state.addedItemsFlatIndex,
          ...justAddedIndex,
        },
        counts: counts,
        deletedIndex: deletedIndex,
        orderIndex: getOrderIndex(state.originalList, addedItemsFlat),
        ...getCounters(counts),
        hasItems: Boolean(state.originalList.length + addedItemsFlat.length),
      };
    }

    case A.LOADS_SEARCH:
    case A.LOADS_SORT: {
      if (action.error) {
        return {
          status: 'error',
        };
      }
      // action.payload is null sometimes, decide to pending.
      if (action.pending || action.payload === null) {
        return {
          status: searchStatuses[action.type],
        };
      }
      if(action.payload === undefined) return;

      const fetchedCount = parseInt(action.payload && action.payload.total_shipments, 10);

      const payloadList = (action.payload && action.payload.shipments || []).map(item => ({
        ...item,
        identity: resourceId('loadsSearchItem', {
          id: item.id,
        }, state.identity),
        parentIdentity: state.identity,
      }));

      const fullList = payloadList;
      const sliced = fullList.slice(0, state.query.limit);

      const itemsIndex = keyBy(fullList, 'id');

      const counts = {
        originalTotal: fetchedCount,
        addedRemoved: 0,
        allRemoved: 0,
        allAdded: 0,
        addedUnreaded: 0,
        addedUnlooked: 0,
      };
      const checkedAll = confirmCheckedAll(sliced);
      const newData = {
        search_since: action.payload && action.payload.search_since,
        fetchedAt: new Date(),
        updatesEnabled: true,
        tooManyUpdates: false,
        deletedIndex: {},
        counts: counts,
        ...getCounters(counts),
        itemsIndex: itemsIndex,
        status: 'ok',
        results: {
          ...action.payload,
          shipments: sliced,
        },
        hasItems: Boolean(fullList.length),
        total: fetchedCount,
        showCount: fetchedCount,
        previewedIndex: itemsIndex,
        originalList: fullList,
        checked: statistyChecked(fullList),
        addedItems: [],
        addedItemsFlat: [],
        addedItemsFlatIndex: {},
        orderIndex: getOrderIndex(sliced, []),
        checkedAll,
      };
      return newData
    }

    case A.LOADS_UPDATE: {

      if (action.error || action.pending) return state;
      if (!state.results || !state.results.shipments || !state.results.shipments.lenght) return state;
      const { data } = action.payload;
      const { id: indexId } = action.meta;
      const shipments = state.results.shipments;
      // Update currect shipment by Id 
      const updateShipments = shipments.map(shipment => {
        const { id } = shipment;
        if (id === indexId) {
          return mergeObjectSafe(shipment, {
            average_rate_price: data.thisWeek || 0
          })
        }
        return shipment;
      })
      return {
        ...state,
        results: {
          ...state.results,
          shipments: updateShipments
        }
      }

    }

    case A.LOADS_BOOKING: {
      if (action.error || action.pending) return state;
      return {
        booking: action.payload.items || []
      }
    }
    case A.LOADS_BID: {
      if (action.error || action.pending) return state;
      return {
        bid: {
          list: action.payload.data ? new Map(action.payload.data.resultList.map(({ shipment, status, id }) => ([shipment.id, { status, id }]))) : new Map(),
          updated: Date.now()
        }
      }
    }
    case LOADS.SEARCH_RESULTS_TOGGLE: {
      const addedItemsFlat = state.addedItemsFlat;
      const viewed = {
        ...(fullState.loads.viewed || {}),
        [action.payload.props.id]: true,
      };

      const counts = {
        ...state.counts,
        addedUnreaded: countNotDone(addedItemsFlat, state.deletedIndex, viewed),
      };

      return {
        counts: counts,
        ...getCounters(counts),
      };
    }
    case A.LOADS_LIST_LIMIT_CHANGED: {
      const shipments = state.originalList.slice(0, state.query.limit);
      const checkedAll = confirmCheckedAll(shipments);
      return {
        results: {
          ...state.results,
          shipments,
        },
        checkedAll,
      };
    }
    case WINDOW.WINDOW_FOCUSED: {
      const counts = {
        ...state.counts,
        addedUnlooked: 0,
      };

      return {
        previewedIndex: state.itemsIndex,
        ...getCounters(counts),
      };
    }
    // hide-loads
    case A.HIDE_THE_LOADS: {
      let originalList = cloneDeep(state.originalList);
      for (let i = 0; i < action.payload.data.length; i++) {
        originalList = filter(originalList, item => item[action.payload.idType] !== action.payload.data[i]);
      }
      const shipments = originalList.slice(0, state.query.limit);
      const results = cloneDeep(state.results);
      const counts = cloneDeep(state.counts);
      results.shipments = shipments;
      counts.originalTotal -= action.payload.data.length;

      const total = counts.originalTotal;
      const showCount = counts.originalTotal;

      return Object.assign(
        {},
        state,
        {
          results,
          originalList,
          counts,
          total: total,
          showCount,
          ...getCounters(counts),
          hasItems: Boolean(originalList.length),
        }
      );
    }
    // hide-broker
    case A.HIDE_THE_COMPANY: {
      let originalList = cloneDeep(state.originalList);
      const oldListLength = originalList.length;
      originalList = filter(originalList,
        item => item.company !== action.payload.data.company || item.broker_id !== action.payload.data.id
      );
      const shipments = originalList.slice(0, state.query.limit);
      const newListLength = originalList.length;
      const results = cloneDeep(state.results);
      const counts = cloneDeep(state.counts);
      results.shipments = shipments;
      const difference = oldListLength - newListLength;
      counts.originalTotal -= difference;

      const total = counts.originalTotal;
      const showCount = counts.originalTotal;

      return Object.assign(
        {},
        state,
        {
          results,
          originalList,
          counts,
          total: total,
          showCount,
          ...getCounters(counts),
          hasItems: Boolean(originalList.length),
        }
      );
    }
    case A.LOADS_COLLECTION_THE_COMPANY: {
      const originalList = cloneDeep(state.originalList);
      const results = state.results;
      for (let i = 0; i < originalList.length; i++) {
        if (originalList[i].broker_id === action.payload.id || originalList[i].company === action.payload.company) {
          originalList[i].checkedBroker = true;
        }
      }
      results.shipments = originalList.slice(0, state.query.limit);
      return Object.assign({}, state, { results, originalList });
    }
    case A.LOADS_NO_COLLECTION_COMPANY: {
      const originalList = cloneDeep(state.originalList);
      const results = state.results;
      for (let i = 0; i < originalList.length; i++) {
        if (originalList[i].broker_id === action.payload.id || originalList[i].company === action.payload.company) {
          originalList[i].checkedBroker = false;
        }
      }
      results.shipments = originalList.slice(0, state.query.limit);
      return Object.assign({}, state, { results, originalList });
    }
    case A.LOADS_CHECKED: {
      return checked(action.payload.id, state);
    }
    case A.LOADS_CHECKED_ALL: {
      return checkedAll(state);
    }
    case A.CLEAR_CHECKED: {
      return {
        checkedAll: false,
      };
    }
    case A.UPDATE_CHECKED: {
      const fullList = state.originalList;
      return {
        checked: statistyChecked(fullList),
      };
    }
    case A.LOADS_INDICATOR_DATA: {

      if (action.error || action.pending) return state;
      const { data } = action.payload;

      return {
        indicatorData: data
      }

    }
    case A.LOADS_CAPACITY_RATIO: {

      if (action.error || action.pending) return state;
      const { data } = action.payload;

      return {
        capacityRatioData: data
      }

    }
    // case A.LOADS_INDICATOR_DATA: {

    //   }
  }

  return null;
};

export default { reducer, initialState };



// WEBPACK FOOTER //
// ./src/modules/loadsSearch/reducer.js